/**
 * SignatureUsagePage — Painel do gestor para acompanhar consumo da conta
 * AssineMais (mestre) por tenant. Base pra cobrar franquia + excedente.
 */

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@payflow/ui";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface UsageByTenantRow {
  tenantId: string;
  tenantNome: string;
  tenantSlug: string;
  usoNoMes: number;
  usoTotal: number;
  ultimoEnvioAt: string | null;
}

interface UsageDailyPoint {
  date: string;
  count: number;
}

interface UsageOverview {
  usoNoMes: number;
  usoMesAnterior: number;
  usoHoje: number;
  usoTotal: number;
  tenantsAtivosNoMes: number;
  daily30d: UsageDailyPoint[];
  porTenant: UsageByTenantRow[];
}

function pctDelta(atual: number, anterior: number): number | null {
  if (!anterior) return null;
  return ((atual - anterior) / anterior) * 100;
}

function formatDate(d: string | null): string {
  if (!d) return "-";
  return new Date(d).toLocaleString("pt-BR");
}

function formatShortDay(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export function SignatureUsagePage() {
  const [data, setData] = useState<UsageOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API_BASE}/v1/admin/billing/signature-usage`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const json = (await res.json()) as { success: boolean; data?: UsageOverview; error?: { message?: string } };
        if (!res.ok || !json.success || !json.data) {
          throw new Error(json.error?.message || "Falha ao carregar uso");
        }
        if (!cancelled) setData(json.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-24 rounded-xl bg-muted" />
          <div className="h-64 rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || "Sem dados"}
        </div>
      </div>
    );
  }

  const deltaMes = pctDelta(data.usoNoMes, data.usoMesAnterior);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl">Uso de assinaturas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Consumo agregado da conta AssineMais mestre. Base pra faturamento
          futuro por tenant.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Uso no mês"
          value={data.usoNoMes}
          delta={deltaMes}
          icon="doc"
          tone="green"
          hint="assinaturas enviadas"
        />
        <KpiCard
          label="Uso hoje"
          value={data.usoHoje}
          icon="today"
          tone="blue"
          hint="envios hoje"
        />
        <KpiCard
          label="Tenants ativos no mês"
          value={data.tenantsAtivosNoMes}
          icon="users"
          tone="violet"
          hint="clientes usando"
        />
        <KpiCard
          label="Total acumulado"
          value={data.usoTotal}
          icon="stack"
          tone="orange"
          hint="desde o início"
        />
      </div>

      {/* Timeline 30 dias */}
      <Card>
        <CardHeader>
          <CardTitle>Envios por dia — últimos 30 dias</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.daily30d} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradEnvios" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatShortDay}
                tick={{ fontSize: 11, fill: "currentColor" }}
                stroke="currentColor"
                className="text-muted-foreground"
              />
              <YAxis
                tick={{ fontSize: 11, fill: "currentColor" }}
                stroke="currentColor"
                className="text-muted-foreground"
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelFormatter={(label) => formatShortDay(String(label))}
              />
              <Area type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2} fill="url(#gradEnvios)" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Ranking por tenant */}
      <Card>
        <CardHeader>
          <CardTitle>Consumo por tenant</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.porTenant.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Nenhum tenant enviou assinaturas ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Tenant</th>
                    <th className="px-4 py-3 text-right">Uso no mês</th>
                    <th className="px-4 py-3 text-right">Total acumulado</th>
                    <th className="px-4 py-3 text-left">Último envio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.porTenant.map((row) => (
                    <tr key={row.tenantId} className="border-b border-border hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-foreground">{row.tenantNome}</div>
                        <div className="text-xs text-muted-foreground">{row.tenantSlug}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold tabular-nums text-green-600 dark:text-green-400">
                        {row.usoNoMes}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {row.usoTotal}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(row.ultimoEnvioAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================ */

interface KpiCardProps {
  label: string;
  value: number;
  delta?: number | null;
  hint?: string;
  icon: "doc" | "today" | "users" | "stack";
  tone: "green" | "blue" | "violet" | "orange";
}

const TONE_MAP: Record<KpiCardProps["tone"], { bg: string; icon: string }> = {
  green: { bg: "bg-green-100 dark:bg-green-900/30", icon: "text-green-600 dark:text-green-400" },
  blue: { bg: "bg-blue-100 dark:bg-blue-900/30", icon: "text-blue-600 dark:text-blue-400" },
  violet: { bg: "bg-violet-100 dark:bg-violet-900/30", icon: "text-violet-600 dark:text-violet-400" },
  orange: { bg: "bg-orange-100 dark:bg-orange-900/30", icon: "text-orange-600 dark:text-orange-400" },
};

function KpiCard({ label, value, delta, hint, icon, tone }: KpiCardProps) {
  const t = TONE_MAP[tone];
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.bg}`}>
          <KpiIcon name={icon} className={`h-6 w-6 ${t.icon}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {value.toLocaleString("pt-BR")}
          </p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {delta != null && (
          <span
            className={`text-xs font-semibold tabular-nums ${
              delta >= 0
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function KpiIcon({ name, className }: { name: KpiCardProps["icon"]; className: string }) {
  const paths: Record<KpiCardProps["icon"], string> = {
    doc: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    today: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    users: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
    stack: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
  };
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={paths[name]} />
    </svg>
  );
}
